import { useId } from 'react';
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Select from '@/components/atoms/Select';

import FormField from './FormField';

interface SelectOption {
    value: string;
    label: string;
}

interface FormSelectProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    label: string;
    options: SelectOption[];
    placeholder?: string;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    errorMessage?: string;
    note?: string | null;
    optional?: boolean;
    disabled?: boolean;
}

// Controlled select wrapper. The reactstrap `Input type="select"` + RHF
// `register` ref-forwarding combo was unreliable for selects (submit-time
// validation read empty even after picking an option), so this wrapper
// always uses `Controller` to keep the picked value mirrored in form state.
export default function FormSelect<TFieldValues extends FieldValues>(
    props: FormSelectProps<TFieldValues>
) {
    const { control, name, label, options, placeholder, rules, errorMessage, note, optional } =
        props;
    const { disabled = false } = props;
    const id = useId();
    const { t } = useTranslation('fishGroups');

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormField
                    htmlFor={id}
                    label={label}
                    optional={optional}
                    note={note}
                    error={
                        fieldState.error
                            ? fieldState.error.message ||
                              errorMessage ||
                              t('actions.errors.required')
                            : null
                    }>
                    <Select
                        id={id}
                        invalid={Boolean(fieldState.error)}
                        disabled={disabled}
                        name={field.name}
                        value={(field.value as string | undefined) ?? ''}
                        onBlur={field.onBlur}
                        onChange={(e) => field.onChange(e.target.value)}>
                        {placeholder !== undefined && <option value="">{placeholder}</option>}
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </FormField>
            )}
        />
    );
}
